import * as React from "react";

import { useLoginApp } from "../login-app-provider";
import { useLoginForm_asController } from "../login-form-provider";
import { useProminentError } from "../prominent-error-provider";

import { useOneTimePasswordSendCodeForm } from "./one-time-password-send-code-form-provider";
import { useOneTimePasswordSendCode } from "./one-time-password-send-code-provider";
import { ONE_TIME_PASSWORD_SEND_CODE_ERROR_MAX_RETRIES_EXCEEDED_MESSAGE } from "./one-time-password-user-experience-first-step-send-code-provider";
import {
  useOneTimePasswordVerifyCodeForm,
  useOneTimePasswordVerifyCodeForm_asController,
} from "./one-time-password-verify-code-form-provider";

interface OneTimePasswordUserExperienceSecondStepVerifyCodeConsumerInterface {
  isLoading?: boolean;
  handleVerifyCode: () => void;
  handleSendCode: () => void;
}
const defaultValue =
  {} as unknown as OneTimePasswordUserExperienceSecondStepVerifyCodeConsumerInterface;
const OneTimePasswordUserExperienceSecondStepVerifyCodeContext =
  React.createContext(defaultValue);

export const ONE_TIME_PASSWORD_SEND_CODE_SUCCESSFUL_SPECIAL_MESSAGE_FOR_STEP_2 =
  "We have sent a new code to...";

export const OneTimePasswordUserExperienceSecondStepVerifyCodeProvider: React.FunctionComponent =
  (props) => {
    const { shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost } =
      useLoginApp();

    const { loginWithOneTimePassword } = useLoginForm_asController();

    const { sendCode } = useOneTimePasswordSendCode();

    const { form: sendCodeForm } = useOneTimePasswordSendCodeForm();
    const { form: verifyCodeForm } = useOneTimePasswordVerifyCodeForm();
    const { isVerifyCodeFormCompleteAndValid } =
      useOneTimePasswordVerifyCodeForm_asController();

    const { setProminentError } = useProminentError();

    const [isLoading, setIsLoading] = React.useState(false);

    const handleVerifyCode: OneTimePasswordUserExperienceSecondStepVerifyCodeConsumerInterface["handleVerifyCode"] =
      React.useCallback(async () => {
        const userHasFilledInTheFormCompletely =
          isVerifyCodeFormCompleteAndValid();
        if (!userHasFilledInTheFormCompletely) {
          return;
        }

        setProminentError(null);
        setIsLoading(true);

        try {
          await loginWithOneTimePassword(
            sendCodeForm.email!,
            verifyCodeForm.oneTimePassword!
          );

          if (shouldLoginUsingTheSpecialAsynchronousApiInsteadOfFormPost) {
            setIsLoading(false);
          } else {
            /**
             * No need to set the loading off ->
             * page will be in the middle of redirecting if all goes well,
             * if anything has gone wrong, it will be handled by the catch block
             */
            // setIsLoading(false);
          }
        } catch (error) {
          console.error(error);
          setIsLoading(false);

          let errorMessage = "An unknown error has occurred.";
          if (error instanceof Error && error.message) {
            errorMessage = error.message;

            if (error.message === "A login and a password are required.") {
              errorMessage = "The code that was entered is invalid.";
            }
          }
          setProminentError(errorMessage);
        }
      }, [
        isVerifyCodeFormCompleteAndValid,
        setIsLoading,
        loginWithOneTimePassword,
        sendCodeForm,
        verifyCodeForm,
        setProminentError,
      ]);

    const handleSendCode: OneTimePasswordUserExperienceSecondStepVerifyCodeConsumerInterface["handleSendCode"] =
      React.useCallback(async () => {
        setProminentError(null);

        setIsLoading(true);

        try {
          const sendCodeResponse = await sendCode(sendCodeForm.email || "");

          if (!sendCodeResponse?.wasSuccessful) {
            if (
              !!sendCodeResponse?.wasUnsuccessfulDueToUserExceedingMaxRetries
            ) {
              throw new Error(
                ONE_TIME_PASSWORD_SEND_CODE_ERROR_MAX_RETRIES_EXCEEDED_MESSAGE
              );
            } else {
              throw new Error();
            }
          }
        } catch (error) {
          console.error(error);
          setIsLoading(false);

          let errorMessage = "An unknown error has occurred.";
          if (error instanceof Error && error.message) {
            errorMessage = error.message;
          }
          setProminentError(errorMessage);

          return;
        }

        setProminentError(
          ONE_TIME_PASSWORD_SEND_CODE_SUCCESSFUL_SPECIAL_MESSAGE_FOR_STEP_2
        );
        setIsLoading(false);
      }, [setIsLoading, sendCode, sendCodeForm, setProminentError]);

    const value: OneTimePasswordUserExperienceSecondStepVerifyCodeConsumerInterface =
      React.useMemo(
        () => ({
          isLoading,
          handleVerifyCode,
          handleSendCode,
        }),
        [isLoading, handleVerifyCode, handleSendCode]
      );
    return (
      <OneTimePasswordUserExperienceSecondStepVerifyCodeContext.Provider
        value={value}
      >
        {props.children || []}
      </OneTimePasswordUserExperienceSecondStepVerifyCodeContext.Provider>
    );
  };

export const useOneTimePasswordUserExperienceSecondStepVerifyCode = () => {
  const value = React.useContext(
    OneTimePasswordUserExperienceSecondStepVerifyCodeContext
  );
  if (value === defaultValue) {
    throw new Error(
      "useOneTimePasswordUserExperienceSecondStepVerifyCode must be used within a OneTimePasswordUserExperienceSecondStepVerifyCodeProvider"
    );
  }
  return value;
};
